import React, { useState, useCallback, useRef, useEffect, useLayoutEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Content,
  ConversationHeaderWrapper,
  ConversationHeader,
  ConversationHeaderLeft,
  ConversationHeaderCenter,
  ConversationHeaderCenterPlaceholder,
  ConversationHeaderTitleWrap,
  ConversationHeaderModelWrap,
  HeaderRightGroup,
  ClickableContainer,
  MessageRow,
  MessageBubbleActions,
  MessageBubbleActionIcon,
  MessageBubble,
  SecondContainer,
  MessageText,
  TooltipTrigger,
  ButtonMenuTrigger,
} from "../../ChatPanel.styled";
import {
  ContainerWithRelative,
  HeaderSectionFullWidth,
  MessagesListWithLoading,
  MessageAttachmentsList,
  OpenDocumentCardWrap,
  ReasoningBarWrap,
  SearchWrapper,
  SearchInputWrapper,
  SearchIconLeft,
  SearchInputWithClearWrap,
  SearchInputField,
  SearchClearButton,
  SearchWrapperCount,
  SearchWrapperIconsGroup,
  SearchWrapperIconButton,
} from "./ConversationView.styled";
import { Typography, ChatInput, ButtonMenu, Icon, Tooltip, ReasoningBar, FileAttachment } from "@ann/web-components";
import { useHomeChat } from "../../../../context";
import { useDocumentDescription, useMessageSearch, useResolveDocumentUuid } from "../../../../hooks";
import DeleteConversationModal from "../../../DeleteConversationModal";
import {
  buildHighlightedHtml,
  buildMarkdownWithHighlights,
  isDocumentLikeContent,
  contentToText,
  extractDocumentTitle,
  extractDocumentDescription,
} from "../../../../utils";
import { AgentMessageContent } from "./components/AgentMessageContent";
import { OpenDocumentCard } from "./components/OpenDocumentCard";
import {
  ActivityIndicator,
  ToolCallStatus,
  MessageStatusBanner,
  SourcesCitation,
  PlanWidget,
  StackProgress,
  StreamingWrap,
} from "../../../streaming";
import {
  CHAT_INPUT_ADD_MENU_ITEMS,
  DOCUMENT_SEARCH_PROPS,
} from "../../constants";
import ChatSkeleton from "../ChatSkeleton";

const OpenDocumentCardWithDescription = ({ cardText, message, onOpenDocument }) => {
  const documentUuid = useResolveDocumentUuid(message);
  const { description: endpointDescription } = useDocumentDescription(documentUuid);

  return (
    <OpenDocumentCard
      title={extractDocumentTitle(cardText)}
      description={endpointDescription ?? extractDocumentDescription(cardText)}
      onOpenDocument={onOpenDocument}
    />
  );
};

const ConversationView = ({
  messages,
  attachments,
  onFilesSelected,
  onAttachmentRemove,
  onClearAttachments,
  onAddMenuChange,
  onModeMenuChange,
  onModeTagClose,
  clearAttachments,
  isLoadingMessages = false,
  documents = [],
  onDocumentSelect,
  onDocumentDelete,
  onDocumentSearchMenuChange,
}) => {
  const {
    sendMessage,
    conversationId,
    selectedAgentId,
    selectedAgent,
    defaultAgentForNewChat,
    defaultAgentIdForNewChat,
    agentIsTyping,
    selectedConversationId,
    deleteConversation,
    isDeletingConversation,
    clearAgent,
    selectConversation,
    openDocumentPanel,
    isDocumentPanelOpen,
    consumeLastOpenedFromMessageId,
    documentCreatedMessageIdsByConversation,
    providers,
    selectedProvider,
    selectedModel,
    isConversationSearchOpen,
    closeConversationSearch,
    isSlideOutMode,
    currentActivity,
    toolCalls,
    sources,
    messageStatus,
    planSteps,
    isStreaming,
  } = useHomeChat();
  const navigate = useNavigate();

  const effectiveAgentId = selectedAgentId ?? defaultAgentIdForNewChat;

  const handleSendMessage = useCallback(
    (messageText) => {
      const list = attachments || [];
      const fileUuids = list
        .map((a) => a.fileUuid || a.document_uuid)
        .filter((uuid) => uuid);
      const attachmentDetails =
        list.length > 0
          ? list.map((a) => ({
              fileUuid: a.fileUuid || a.document_uuid,
              name: a.name ?? "Document",
              fileType: a.fileType ?? (a.name?.includes?.(".") ? a.name.split(".").pop() : ""),
              isLoading: a.isLoading === true,
            }))
          : undefined;
      sendMessage(messageText, {
        conversationId,
        agentId: effectiveAgentId,
        fileUuids: fileUuids.length > 0 ? fileUuids : undefined,
        attachmentDetails,
      });
      onClearAttachments?.();
    },
    [
      sendMessage,
      conversationId,
      effectiveAgentId,
      attachments,
      onClearAttachments,
    ],
  );

  const {
    isSearchOpen,
    searchValue,
    currentMatchIndex,
    matches,
    handleSearchClick,
    handleCloseSearch,
    handleSearchChange,
    handleChevronDown,
    handleChevronUp,
    setMessageRef,
  } = useMessageSearch(messages);

  useEffect(() => {
    if (isConversationSearchOpen && !isSearchOpen) handleSearchClick();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConversationSearchOpen]);

  const searchInputRef = useRef(null);

  const handleCloseSearchWithSync = useCallback(() => {
    handleCloseSearch();
    closeConversationSearch();
  }, [handleCloseSearch, closeConversationSearch]);

  useEffect(() => {
    if (isSearchOpen) {
      requestAnimationFrame(() => searchInputRef.current?.focus());
    }
  }, [isSearchOpen]);

  const agentName = useMemo(
    () => selectedAgent?.name ?? defaultAgentForNewChat?.name ?? "Agent GPT 5.2",
    [selectedAgent?.name, defaultAgentForNewChat?.name]
  );
  const modelLabel = useMemo(() => {
    if (providers?.length && selectedProvider && selectedModel) {
      const providerEntry = providers.find((p) => p.provider === selectedProvider);
      const modelEntry = providerEntry?.models?.find((m) => m.value === selectedModel);
      if (modelEntry?.label) return modelEntry.label;
    }
    return selectedAgent?.metadata?.model ?? defaultAgentForNewChat?.metadata?.model ?? "5.2";
  }, [
    providers,
    selectedProvider,
    selectedModel,
    selectedAgent?.metadata?.model,
    defaultAgentForNewChat?.metadata?.model,
  ]);

  const [isTypewriterTyping, setIsTypewriterTyping] = useState(false);
  const handleTypewriterStateChange = useCallback((typing) => {
    setIsTypewriterTyping(typing);
  }, []);

  const isVisuallyTyping = agentIsTyping || isTypewriterTyping;

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const handleMenuChange = useCallback(
    (value) => {
      if (value === "new_chat") {
        clearAgent();
        selectConversation(null);
      }
      if (value === "edit_agent" && selectedAgentId) {
        navigate(`/v2/playground2/${selectedAgentId}/configuration`);
      }
      if (value === "delete_chat") setIsDeleteModalOpen(true);
    },
    [selectedAgentId, navigate, clearAgent, selectConversation]
  );
  const handleCloseDeleteModal = useCallback(() => setIsDeleteModalOpen(false), []);
  const handleConfirmDelete = useCallback(
    async (id) => {
      await deleteConversation(id);
      handleCloseDeleteModal();
    },
    [deleteConversation, handleCloseDeleteModal]
  );

  const handleOpenMessageAsDocument = useCallback(
    (messageContent, messageId = null) => {
      const text = contentToText(messageContent);
      openDocumentPanel({
        content: text,
        messageId,
      });
    },
    [openDocumentPanel]
  );

  const handleDocumentIconClick = useCallback(
    (messageId, messageContent) => {
      handleOpenMessageAsDocument(messageContent, messageId);
    },
    [handleOpenMessageAsDocument]
  );

  const handleCopyMessage = useCallback(
    (content) => {
      const text = contentToText(content);
      if (!text) return;
      navigator.clipboard.writeText(text).catch(() => {});
    },
    []
  );

  const messagesListRef = useRef(null);
  const isUserScrolledUpRef = useRef(false);
  const lastScrollTopRef = useRef(0);
  const prevAgentIsTypingRef = useRef(agentIsTyping);

  const getScrollableElement = useCallback(() => {
    const root = messagesListRef.current;
    if (!root) return null;
    if (root.scrollHeight > root.clientHeight) return root;
    for (let i = 0; i < root.children?.length; i++) {
      const child = root.children[i];
      if (child.scrollHeight > child.clientHeight) return child;
    }
    return root;
  }, []);

  const handleScroll = useCallback(() => {
    const el = getScrollableElement();
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    const isAtBottom = distanceFromBottom <= 20;
    if (scrollTop < lastScrollTopRef.current) isUserScrolledUpRef.current = true;
    if (isAtBottom) isUserScrolledUpRef.current = false;
    lastScrollTopRef.current = scrollTop;
  }, [getScrollableElement]);

  const scrollToBottom = useCallback(() => {
    const el = getScrollableElement();
    if (el) el.scrollTop = el.scrollHeight;
  }, [getScrollableElement]);

  useEffect(() => {
    if (isUserScrolledUpRef.current) return;
    requestAnimationFrame(() => requestAnimationFrame(scrollToBottom));
  }, [messages?.length, isVisuallyTyping, scrollToBottom]);

  useEffect(() => {
    const wasTyping = prevAgentIsTypingRef.current;
    prevAgentIsTypingRef.current = isVisuallyTyping;
    if (wasTyping && !isVisuallyTyping && !isUserScrolledUpRef.current) {
      requestAnimationFrame(() => scrollToBottom());
    }
  }, [isVisuallyTyping, scrollToBottom]);

  // While content is visually growing (either streaming from WS or typewriter animation), keep scroll at bottom.
  useEffect(() => {
    if (!isVisuallyTyping) return;
    const id = setInterval(() => {
      if (isUserScrolledUpRef.current) return;
      scrollToBottom();
    }, 80);
    return () => clearInterval(id);
  }, [isVisuallyTyping, scrollToBottom]);

  useEffect(() => {
    isUserScrolledUpRef.current = false;
    lastScrollTopRef.current = 0;
    if (selectedConversationId) {
      const t = setTimeout(scrollToBottom, 100);
      return () => clearTimeout(t);
    }
  }, [selectedConversationId, scrollToBottom]);

  useLayoutEffect(() => {
    if (!selectedConversationId || !messages?.length) return;
    scrollToBottom();
  }, [selectedConversationId, messages?.length, scrollToBottom]);

  useEffect(() => {
    const el = getScrollableElement();
    if (!el) return;
    el.addEventListener("scroll", handleScroll, { passive: true });
    return () => el.removeEventListener("scroll", handleScroll);
  }, [messages?.length, getScrollableElement, handleScroll]);

  return (
    <ContainerWithRelative>
      {/* Loading overlay */}
      <ChatSkeleton show={isLoadingMessages} />
      <HeaderSectionFullWidth>
        {!isSlideOutMode && (
          <ConversationHeader>
            <ConversationHeaderLeft>
              <ConversationHeaderTitleWrap>
                <Typography category="body" size="medium" weight="bold">
                  {agentName}
                </Typography>
              </ConversationHeaderTitleWrap>
              <ConversationHeaderModelWrap>
                <Typography category="body" size="medium" weight="regular" color="body">
                  {modelLabel}
                </Typography>
              </ConversationHeaderModelWrap>
            </ConversationHeaderLeft>
            <ConversationHeaderCenter aria-hidden>
              <ConversationHeaderCenterPlaceholder>
                Search in conversation or ask something to get started and find answers
              </ConversationHeaderCenterPlaceholder>
            </ConversationHeaderCenter>
            <HeaderRightGroup>
              <ClickableContainer onClick={handleSearchClick}>
                <Tooltip
                  content="Search in conversation"
                  zIndex={99999999}
                  position={"bottom"}
                  arrow={false}
                >
                  <Icon color="neutral" name="search" size="medium" />
                </Tooltip>
              </ClickableContainer>
              <Tooltip
                content="Options"
                zIndex={99999999}
                position={"bottom"}
                arrow={false}
              >
                <ButtonMenuTrigger>
                  <ButtonMenu
                    alignMenu="left"
                    customIcon="dotsMenu2"
                    iconPosition="right"
                    label=""
                    menuPosition="bottom"
                    onChange={handleMenuChange}
                    size="small"
                    type="tertiary"
                    usePortal
                    width="auto"
                    disableEffects
                    className="conversation-header-actions"
                  >
                    <ButtonMenu.Menu icon={true}>
                      <ButtonMenu.Item label="New chat" value="new_chat" icon="edit_square">
                        New chat
                      </ButtonMenu.Item>
                      <ButtonMenu.Item label="Edit Agent" value="edit_agent" icon="edit">
                        Edit Agent
                      </ButtonMenu.Item>
                      {/* <ButtonMenu.Item label="Delete chat" value="delete_chat" icon="delete2">
                        Delete chat
                      </ButtonMenu.Item> */}
                    </ButtonMenu.Menu>
                  </ButtonMenu>
                </ButtonMenuTrigger>
              </Tooltip>
            </HeaderRightGroup>
          </ConversationHeader>
        )}
        <SearchWrapper $hidden={!isSearchOpen}>
          <SearchInputWrapper>
            <SearchIconLeft aria-hidden>
              <Icon name="search" size="custom" width="16px" height="16px" />
            </SearchIconLeft>
            <SearchInputWithClearWrap>
              <SearchInputField
                ref={searchInputRef}
                type="text"
                value={searchValue}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder="Search in conversation..."
                aria-label="Search in conversation"
              />
              <SearchClearButton
                type="button"
                onClick={handleCloseSearchWithSync}
                aria-label="Close search"
              >
                <Icon name="close" size="small" />
              </SearchClearButton>
            </SearchInputWithClearWrap>
          </SearchInputWrapper>
          <SearchWrapperCount>
            <Typography category="body" size="large" weight="regular" color="body">
              {matches.length > 0
                ? `${currentMatchIndex + 1} of ${matches.length}`
                : "0 of 0"}
            </Typography>
          </SearchWrapperCount>
          <SearchWrapperIconsGroup>
            <SearchWrapperIconButton type="button" onClick={handleChevronDown} aria-label="Previous match">
              <Icon color="neutral" name="chevronDown" size="medium" />
            </SearchWrapperIconButton>
            <SearchWrapperIconButton type="button" onClick={handleChevronUp} aria-label="Next match">
              <Icon color="neutral" name="chevronUp" size="medium" />
            </SearchWrapperIconButton>
          </SearchWrapperIconsGroup>
        </SearchWrapper>
      </HeaderSectionFullWidth>
      <Content>
        <ConversationHeaderWrapper>
          <MessagesListWithLoading
            parentRef={messagesListRef}
            data-messages-list
            onScroll={handleScroll}
            $isLoading={isLoadingMessages}
          >
            {(() => {
              const lastUserMessageIndex = (() => {
                for (let i = messages.length - 1; i >= 0; i--) {
                  if (messages[i].role === "user") return i;
                }
                return -1;
              })();
              const lastMessageIsFromUser = messages.length > 0 && messages[messages.length - 1].role === "user";
              return messages.map((msg, index) => {
              const isUser = msg.role === "user";
              const isLastAgentMessage = !isUser && index === messages.length - 1;
              const isStreamingMessage = !isUser && (agentIsTyping || isStreaming) && isLastAgentMessage;
              const isActivelyTyping = !isUser && isVisuallyTyping && isLastAgentMessage;
              const showPlaceholderAgentBubble =
                isUser &&
                index === lastUserMessageIndex &&
                agentIsTyping &&
                lastMessageIsFromUser;
              const showStreamingComponents = isStreamingMessage || showPlaceholderAgentBubble;
              const messageContent = msg.content || "";
              const matchesInMessage = searchValue.trim()
                ? matches.filter((m) => m.messageIndex === index)
                : [];
              const hasMatches = matchesInMessage.length > 0;
              const userHighlightedHtml =
                isUser && hasMatches
                  ? buildHighlightedHtml(
                      messageContent,
                      matchesInMessage,
                      currentMatchIndex
                    )
                  : null;
              const agentHighlightedMarkdown =
                !isUser && hasMatches
                  ? buildMarkdownWithHighlights(
                      messageContent,
                      matchesInMessage,
                      currentMatchIndex
                    )
                  : null;

              const isDocumentMessage =
                !isUser && (msg.complexAnswer || isDocumentLikeContent(msg.content));
              const documentCreatedIds = documentCreatedMessageIdsByConversation[conversationId] || [];
              const showOpenDocumentCard = !isUser && documentCreatedIds.includes(msg.id);
              const cardText = showOpenDocumentCard ? contentToText(msg.content) : null;

              return (
                <React.Fragment key={index}>
                  <MessageRow $isUser={isUser} ref={setMessageRef(index)}>
                    <MessageBubble $isUser={isUser}>
                      {isUser ? (
                        <>
                          {userHighlightedHtml !== null ? (
                            <MessageText
                              dangerouslySetInnerHTML={{ __html: userHighlightedHtml }}
                              role="text"
                            />
                          ) : (
                            <Typography category="body" size="medium" weight="regular">
                              {msg.content}
                            </Typography>
                          )}
                          {msg.attachments?.length > 0 && (
                            <MessageAttachmentsList>
                              {msg.attachments.map((att, attIndex) => (
                                <FileAttachment
                                  key={att.fileUuid ?? att.id ?? attIndex}
                                  name={att.name ?? "Document"}
                                  type={att.fileType ?? ""}
                                  viewOnly
                                  isLoading={att.isLoading === true}
                                />
                              ))}
                            </MessageAttachmentsList>
                          )}
                        </>
                      ) : (
                        <>
                          <AgentMessageContent
                            content={msg.content}
                            highlightMarkdown={agentHighlightedMarkdown}
                            isStreaming={isStreamingMessage}
                            onTypingStateChange={isLastAgentMessage ? handleTypewriterStateChange : undefined}
                          />
                          {showStreamingComponents && (
                            <StreamingWrap>
                              <ActivityIndicator activity={currentActivity} />
                              <StackProgress activity={currentActivity} />
                              <ToolCallStatus toolCalls={toolCalls} />
                              {isStreamingMessage && (
                                <>
                                  <PlanWidget planSteps={planSteps} />
                                  <SourcesCitation sources={sources} />
                                </>
                              )}
                            </StreamingWrap>
                          )}
                          {(isActivelyTyping) && (
                            <ReasoningBarWrap>
                              <ReasoningBar speed="2s" width="100%" />
                            </ReasoningBarWrap>
                          )}
                        </>
                      )}
                    {isUser ? (
                      <MessageBubbleActions>
                        <Tooltip
                          content="Copy"
                          zIndex={99999999}
                          arrow={false}
                        >
                          <TooltipTrigger>
                            <MessageBubbleActionIcon
                              onClick={() => handleCopyMessage(msg.content)}
                              role="button"
                              aria-label="Copy"
                            >
                              <Icon color="neutral-black" name="copy" size="small" />
                            </MessageBubbleActionIcon>
                          </TooltipTrigger>
                        </Tooltip>
                      </MessageBubbleActions>
                    ) : (
                      <MessageBubbleActions>
                        <Tooltip
                          content="Copy"
                          zIndex={99999999}
                          arrow={false}
                        >
                          <TooltipTrigger>
                            <MessageBubbleActionIcon
                              onClick={() => handleCopyMessage(msg.content)}
                              role="button"
                              aria-label="Copy"
                            >
                              <Icon color="neutral-black" name="copy" size="small" />
                            </MessageBubbleActionIcon>
                          </TooltipTrigger>
                        </Tooltip>
                        {isDocumentMessage && (
                          <Tooltip
                            content="Open as doc"
                            zIndex={99999999}
                            arrow={false}
                          >
                            <TooltipTrigger>
                              <MessageBubbleActionIcon
                                onClick={() => handleDocumentIconClick(msg.id, msg.content)}
                                role="button"
                                aria-label="Open as document"
                              >
                                <Icon color="neutral-black" name="article" size="small" />
                              </MessageBubbleActionIcon>
                            </TooltipTrigger>
                          </Tooltip>
                        )}
                      </MessageBubbleActions>
                    )}
                  </MessageBubble>
                  {showOpenDocumentCard && cardText != null && (
                    <OpenDocumentCardWrap>
                      <OpenDocumentCardWithDescription
                        cardText={cardText}
                        message={msg}
                        onOpenDocument={() => handleOpenMessageAsDocument(msg.content, msg.id)}
                      />
                    </OpenDocumentCardWrap>
                  )}
                </MessageRow>
                {showPlaceholderAgentBubble && (
                  <MessageRow $isUser={false}>
                    <MessageBubble $isUser={false}>
                      <StreamingWrap>
                        <ActivityIndicator activity={currentActivity} />
                        <StackProgress activity={currentActivity} />
                        <ToolCallStatus toolCalls={toolCalls} />
                      </StreamingWrap>
                      <ReasoningBarWrap>
                        <ReasoningBar speed="2s" width="100%" />
                      </ReasoningBarWrap>
                    </MessageBubble>
                  </MessageRow>
                )}
                {!isUser && isLastAgentMessage && messageStatus && (
                  <MessageRow $isUser={false}>
                    <MessageBubble $isUser={false}>
                      <MessageStatusBanner status={messageStatus} />
                    </MessageBubble>
                  </MessageRow>
                )}
              </React.Fragment>
              );
            });
            })()}
          </MessagesListWithLoading>
        </ConversationHeaderWrapper>
        <DeleteConversationModal
          isOpen={isDeleteModalOpen}
          onClose={handleCloseDeleteModal}
          onConfirm={handleConfirmDelete}
          conversationId={selectedConversationId}
          isLoading={isDeletingConversation}
        />
        <SecondContainer>
          <ChatInput
            className={"chat-input"}
            placeholder="Ask anything"
            allowUploadFiles
            attachments={attachments}
            onFilesSelected={onFilesSelected}
            onAttachmentRemove={onAttachmentRemove}
            onAddMenuChange={onAddMenuChange}
            onModeMenuChange={onModeMenuChange}
            onModeTagClose={onModeTagClose}
            addMenuItems={CHAT_INPUT_ADD_MENU_ITEMS}
            documentSearchProps={DOCUMENT_SEARCH_PROPS}
            documents={documents}
            onDocumentSelect={onDocumentSelect}
            onDocumentDelete={onDocumentDelete}
            onDocumentSearchDeleteClick={onDocumentDelete}
            onDocumentSearchMenuChange={onDocumentSearchMenuChange}
            isProcessing={agentIsTyping}
            onSend={handleSendMessage}
          />
        </SecondContainer>
      </Content>
    </ContainerWithRelative>
  );
};

export default ConversationView;
